import { useMemo } from 'react';
import { keepPreviousData } from '@zg-rentals/react-query5';
import { useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments } from '@zg-rentals/ts-capps-client-react-query';
import type { Document } from '../models/rental-application';
import { isRedactionCheckComplete, toDocument } from './useRetrieveActiveDocuments';

const MAX_REFETCH_ATTEMPTS = 10;
// Less than 1 second is forbiden by the API spec
const REFETCH_INTERVAL = 6000;

/**
 * Keeps polling redaction related fields for the given income documents and returns them with those
 * fields filled in, along with whether any of them have expired. Documents without an id yet, such as
 * an upload still in flight, are ignored
 */
export function useIncomeDocsRedaction(documents: Array<Document>) {
  const documentIds = documents.map((doc) => doc.id).filter((id): id is string => !!id);

  const { data } = useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments(
    { documentIds },
    {
      enabled: documentIds.length > 0,
      // The document ids are part of the query key, without this the redaction info would blank out
      // on every upload and deletion while the new key loads
      placeholderData: keepPreviousData,
      // Poll until every redaction check has finished, giving up after MAX_REFETCH_ATTEMPTS. Adding or
      // removing a document changes the query key, so it starts over with a fresh attempt count
      refetchInterval: ({ state }) =>
        state.data?.documentInfoList.every(isRedactionCheckComplete) || state.dataUpdateCount >= MAX_REFETCH_ATTEMPTS
          ? false
          : REFETCH_INTERVAL,
      meta: {
        tags: ['incomeDocuments'],
        context: { api: 'cappsRenterDocuments', details: 'retrieveActiveDocuments' },
      },
    },
  );

  const activeDocuments = useMemo(() => data?.documentInfoList.map(toDocument), [data]);

  // keepPreviousData keeps the last response around once every document is gone, so don't trust it then
  const areDocumentsExpired = documentIds.length > 0 && (data?.areDocumentsExpired ?? false);

  return { documents: activeDocuments, areDocumentsExpired };
}
